import { call, put, takeLatest } from 'redux-saga/effects'
import { AdminRunReconciliation } from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  ReconciliationRun,
  RunReconciliationPayload,
  RunReconciliationResponse,
} from 'src/interfaces/reconciliation'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

export function* onRunReconciliation(action: AsyncAction<RunReconciliationPayload>) {
  const { partnerId, startDate, endDate } = action.payload

  try {
    // Synchronous and potentially slow: pulls US Bank transactions for the range, syncs and
    // compares. All three fields are required (`@IsNotEmpty`, dates `@IsDateString`).
    const { payload }: { payload: RunReconciliationResponse } = yield call(
      callSecureApi,
      Endpoints.AdminRunReconciliation,
      { method: 'POST', body: { partnerId, startDate, endDate } },
    )

    const run: ReconciliationRun = {
      request: { partnerId, startDate, endDate },
      result: {
        warnings: payload?.result?.warnings ?? [],
        errors: payload?.result?.errors ?? [],
      },
    }

    yield put(AdminRunReconciliation.success(run))

    if (action.next) {
      action.next(null, run)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.reconciliation.onRunReconciliation] error' })

    yield put(AdminRunReconciliation.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Reconciliation failed'))
    }
  }
}

export function* reconciliationSaga() {
  yield takeLatest(AdminRunReconciliation.type.REQUEST, onRunReconciliation)
}
